/**
 * exportChat.js
 * Utilities for exporting the current chat conversation as PDF, CSV, or TXT.
 *
 * PDF  – Opens a browser print dialog pre-styled for a clean printout.
 *         Charts are pre-rendered to PNG via vega-embed and embedded inline.
 * CSV  – Exports all table-type messages as CSV files (one per table).
 *         If there are no tables, exports Q&A pairs as CSV.
 * TXT  – Plain-text transcript of the conversation.
 */
import embed from 'vega-embed';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Convert an array of row objects to a CSV string.
 * Values are quoted if they contain commas, quotes, or newlines.
 */
function objectsToCSV(rows) {
  if (!rows || rows.length === 0) return '';
  const headers = Object.keys(rows[0]);
  const escape = (val) => {
    const s = val == null ? '' : String(val);
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const lines = [
    headers.map(escape).join(','),
    ...rows.map(row => headers.map(h => escape(row[h])).join(',')),
  ];
  return lines.join('\r\n');
}

/** Trigger a browser file download. */
function downloadFile(filename, content, mimeType) {
  // Prepend UTF-8 BOM for CSV files so Excel detects encoding correctly
  const bom = mimeType.includes('csv') ? '\uFEFF' : '';
  const blob = new Blob([bom + content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Build a safe filename base from the genie name + current date. */
function buildFilenameBase(genieName) {
  const safe = (genieName || 'Chat').replace(/[^a-zA-Z0-9 _-]/g, '').trim().replace(/\s+/g, '_');
  const date = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  return `${safe}_${date}`;
}

// ─── Strip markdown ───────────────────────────────────────────────────────────

/**
 * Very lightweight markdown → plain-text strip.
 * Handles: **bold**, *italic*, `code`, headers (#), links, blockquotes, lists.
 */
function stripMarkdown(text) {
  if (!text) return '';
  return text
    .replace(/!\[.*?\]\(.*?\)/g, '[image]')      // images
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')      // links → label only
    .replace(/^#{1,6}\s+/gm, '')                   // headings
    .replace(/\*\*\*(.+?)\*\*\*/g, '$1')           // bold+italic
    .replace(/\*\*(.+?)\*\*/g, '$1')               // bold
    .replace(/\*(.+?)\*/g, '$1')                   // italic
    .replace(/`{1,3}[^`]*`{1,3}/g, (m) => m.replace(/`/g, '')) // code
    .replace(/^>\s+/gm, '')                         // blockquotes
    .replace(/^[-*+]\s+/gm, '- ')                  // unordered lists (plain ASCII)
    .replace(/^\d+\.\s+/gm, (m) => m)              // ordered lists – keep numbering
    .replace(/\n{3,}/g, '\n\n')                    // collapse excess blank lines
    .trim();
}

// ─── TXT export ───────────────────────────────────────────────────────────────

/**
 * Export all messages as a plain-text transcript.
 * @param {Array}  messages  - Array of message objects from GenericChatPage state.
 * @param {string} genieName - Currently selected genie name (used in filename/header).
 * @param {string} userName  - Display name of the current user.
 */
export function exportAsTXT(messages, genieName, userName) {
  const filename = `${buildFilenameBase(genieName)}.txt`;
  const lines = [];

  lines.push(`Chat Export – ${genieName || 'AI Studio'}`);
  lines.push(`User: ${userName || 'User'}`);
  lines.push(`Date: ${new Date().toLocaleString()}`);
  lines.push('='.repeat(60));
  lines.push('');

  const contentMessages = messages.filter(m => m.role !== 'status');

  contentMessages.forEach((msg) => {
    if (msg.role === 'user') {
      lines.push(`You:`);
      lines.push(msg.content || '');
      lines.push('');
    } else if (msg.role === 'assistant') {
      if (msg.type === 'table') {
        lines.push(`Assistant [Table]:`);
        if (msg.headers && msg.data) {
          lines.push(msg.headers.join('\t'));
          msg.data.forEach(row => {
            lines.push(msg.headers.map(h => (row[h] ?? '')).join('\t'));
          });
        }
        lines.push('');
      } else if (msg.type === 'chart') {
        lines.push(`Assistant [Chart]:`);
        lines.push(msg.data?.description || '(Chart – see PDF export for visual)');
        lines.push('');
      } else if (msg.type === 'suggestions') {
        // skip suggestions in plain text export
      } else {
        lines.push(`Assistant:`);
        lines.push(stripMarkdown(msg.content || ''));
        lines.push('');
      }
    }
  });

  lines.push('='.repeat(60));
  lines.push(`Exported from AI Studio`);

  downloadFile(filename, lines.join('\n'), 'text/plain;charset=utf-8');
}

// ─── CSV export ───────────────────────────────────────────────────────────────

/**
 * Export the full chat as a single CSV file.
 * All Q&A pairs are exported as a two-column (Question / Answer) CSV.
 * Table-type messages are inlined as pipe-separated rows within the Answer column.
 * @param {Array}  messages  - Message array from state.
 * @param {string} genieName - Currently selected genie name.
 */
export function exportAsCSV(messages, genieName) {
  const base = buildFilenameBase(genieName);

  const rows = [];
  let lastQuestion = '';
  messages
    .filter(m => m.role !== 'status' && m.type !== 'suggestions')
    .forEach(msg => {
      if (msg.role === 'user') {
        lastQuestion = msg.content || '';
      } else if (msg.role === 'assistant') {
        let answer = '';
        if (msg.type === 'table') {
          // Inline the table data as pipe-separated rows within the cell
          if (msg.data && msg.data.length > 0) {
            const headers = msg.headers || Object.keys(msg.data[0]);
            const headerRow = headers.join(' | ');
            const dataRows = msg.data.map(row => headers.map(h => row[h] ?? '').join(' | '));
            answer = [headerRow, ...dataRows].join('\n');
          } else {
            answer = '[Table – no data]';
          }
        } else if (msg.type === 'chart') {
          answer = `[Chart: ${msg.data?.description || 'Chart'}]`;
        } else {
          answer = stripMarkdown(msg.content || '');
        }
        if (lastQuestion || answer) {
          rows.push({ Question: lastQuestion, Answer: answer });
        }
        lastQuestion = '';
      }
    });

  if (rows.length > 0) {
    const csv = objectsToCSV(rows);
    downloadFile(`${base}_transcript.csv`, csv, 'text/csv;charset=utf-8');
  }
}

// ─── PDF export ───────────────────────────────────────────────────────────────

/**
 * Render a Vega-Lite spec to a PNG data URL using an off-screen container.
 * Returns null on failure so the caller can fall back gracefully.
 */
async function vegaSpecToPngDataUrl(spec) {
  const container = document.createElement('div');
  container.style.cssText =
    'position:fixed;left:-9999px;top:-9999px;width:700px;height:400px;visibility:hidden;';
  document.body.appendChild(container);
  try {
    const result = await embed(container, spec, { actions: false, renderer: 'canvas' });
    const dataUrl = await result.view.toImageURL('png', 2);
    result.view.finalize();
    return dataUrl;
  } catch (err) {
    console.error('[PDF EXPORT] Failed to render chart to PNG:', err);
    return null;
  } finally {
    document.body.removeChild(container);
  }
}

/**
 * Export the conversation as a PDF using the browser's built-in print dialog.
 * Charts are pre-rendered to PNG via vega-embed and embedded as <img> tags.
 * Tables are rendered as styled HTML tables.
 * Text content uses a minimal markdown → HTML converter.
 *
 * @param {Array}  messages  - Message array from state.
 * @param {string} genieName - Currently selected genie name.
 * @param {string} userName  - Display name of the current user.
 */
export async function exportAsPDF(messages, genieName, userName) {
  const contentMessages = messages.filter(
    m => m.role !== 'status' && m.type !== 'suggestions'
  );

  // Pre-render all chart messages to PNG data URLs before opening the print window
  const chartPngMap = new Map(); // index in contentMessages → PNG data URL
  for (let i = 0; i < contentMessages.length; i++) {
    const msg = contentMessages[i];
    if (msg.role === 'assistant' && msg.type === 'chart' && msg.data) {
      const dataUrl = await vegaSpecToPngDataUrl(msg.data);
      if (dataUrl) chartPngMap.set(i, dataUrl);
    }
  }

  // Build table HTML from data rows
  const buildTableHTML = (msg) => {
    if (!msg.data || msg.data.length === 0) return '<p><em>No data</em></p>';
    const headers = msg.headers || Object.keys(msg.data[0]);
    const thead = `<tr>${headers.map(h => `<th>${escapeHtml(h)}</th>`).join('')}</tr>`;
    const tbody = msg.data
      .map(row => `<tr>${headers.map(h => `<td>${escapeHtml(row[h] ?? '')}</td>`).join('')}</tr>`)
      .join('');
    return `<table><thead>${thead}</thead><tbody>${tbody}</tbody></table>`;
  };

  // Minimal markdown → HTML (headers, bold, italic, code, lists, links)
  const markdownToHTML = (text) => {
    if (!text) return '';
    let html = text
      // headings
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^# (.+)$/gm, '<h1>$1</h1>')
      // bold + italic
      .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      // inline code
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      // links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
      // images → text label
      .replace(/!\[([^\]]*)\]\([^)]+\)/g, '[image: $1]')
      // blockquotes
      .replace(/^> (.+)$/gm, '<blockquote>$1</blockquote>')
      // unordered lists
      .replace(/^[-*+] (.+)$/gm, '<li>$1</li>')
      // ordered lists
      .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
      // newlines → <br> (only for non-block elements)
      .replace(/\n/g, '<br>');
    // Wrap consecutive <li> with <ul>
    html = html.replace(/(<li>.*?<\/li>(<br>)?)+/g, (m) => `<ul>${m.replace(/<br>/g, '')}</ul>`);
    return html;
  };

  // Build message HTML (index needed for chartPngMap lookup)
  const messageHTML = contentMessages.map((msg, i) => {
    if (msg.role === 'user') {
      return `
        <div class="msg user-msg">
          <div class="msg-label">You</div>
          <div class="msg-content">${markdownToHTML(msg.content || '')}</div>
        </div>`;
    } else if (msg.role === 'assistant') {
      if (msg.type === 'table') {
        return `
          <div class="msg assistant-msg">
            <div class="msg-label">Assistant</div>
            <div class="msg-content table-wrap">${buildTableHTML(msg)}</div>
          </div>`;
      } else if (msg.type === 'chart') {
        const pngUrl = chartPngMap.get(i);
        if (pngUrl) {
          const title = (typeof msg.data?.title === 'string' ? msg.data.title : null)
            || msg.data?.description
            || 'Chart';
          return `
            <div class="msg assistant-msg">
              <div class="msg-label">Assistant [Chart]</div>
              <div class="msg-content chart-wrap">
                <p class="chart-title">${escapeHtml(title)}</p>
                <img src="${pngUrl}" alt="${escapeHtml(title)}" style="max-width:100%;height:auto;border-radius:6px;" />
              </div>
            </div>`;
        }
        // Fallback if rendering failed
        return `
          <div class="msg assistant-msg">
            <div class="msg-label">Assistant</div>
            <div class="msg-content chart-placeholder">
              &#128202; <em>Chart: ${escapeHtml(msg.data?.description || 'Visualization')}</em>
              <br><small>Chart could not be rendered for export.</small>
            </div>
          </div>`;
      } else {
        return `
          <div class="msg assistant-msg">
            <div class="msg-label">Assistant</div>
            <div class="msg-content">${markdownToHTML(msg.content || '')}</div>
          </div>`;
      }
    }
    return '';
  }).join('');

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Chat Export – ${escapeHtml(genieName || 'AI Studio')}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: 'Segoe UI', Arial, sans-serif;
      font-size: 13px;
      color: #111;
      background: #fff;
      padding: 32px 40px;
      line-height: 1.6;
    }
    .export-header {
      border-bottom: 2px solid #FF901D;
      padding-bottom: 16px;
      margin-bottom: 24px;
    }
    .export-header h1 {
      font-size: 20px;
      font-weight: 700;
      color: #111;
      margin-bottom: 4px;
    }
    .export-meta {
      font-size: 12px;
      color: #666;
    }
    .msg {
      margin-bottom: 18px;
      padding: 12px 16px;
      border-radius: 8px;
      page-break-inside: avoid;
    }
    .user-msg {
      background: #f5f5f5;
      border-left: 3px solid #888;
      margin-left: auto;
      max-width: 80%;
    }
    .assistant-msg {
      background: #fff9f4;
      border-left: 3px solid #FF901D;
      max-width: 90%;
    }
    .msg-label {
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: #888;
      margin-bottom: 6px;
    }
    .user-msg .msg-label { color: #555; }
    .assistant-msg .msg-label { color: #FF901D; }
    .msg-content h1 { font-size: 16px; margin: 8px 0 4px; }
    .msg-content h2 { font-size: 15px; margin: 8px 0 4px; }
    .msg-content h3 { font-size: 14px; margin: 6px 0 4px; }
    .msg-content strong { font-weight: 700; }
    .msg-content em { font-style: italic; }
    .msg-content code {
      background: #f0f0f0;
      padding: 1px 4px;
      border-radius: 3px;
      font-family: monospace;
      font-size: 12px;
    }
    .msg-content blockquote {
      border-left: 3px solid #ccc;
      padding-left: 10px;
      color: #555;
      margin: 6px 0;
    }
    .msg-content ul, .msg-content ol { padding-left: 20px; margin: 6px 0; }
    .msg-content a { color: #1a73e8; }
    .chart-placeholder {
      color: #666;
      font-style: italic;
      background: #f9f9f9;
      border: 1px dashed #ccc;
      padding: 12px;
      border-radius: 6px;
    }
    .chart-wrap { padding: 4px 0; }
    .chart-title {
      font-size: 12px;
      font-weight: 600;
      color: #555;
      margin-bottom: 8px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
      margin-top: 8px;
    }
    th {
      background: #FF901D;
      color: #fff;
      padding: 7px 10px;
      text-align: left;
      font-weight: 600;
    }
    td {
      padding: 6px 10px;
      border-bottom: 1px solid #e8e8e8;
      color: #222;
    }
    tr:nth-child(even) td { background: #fafafa; }
    .export-footer {
      margin-top: 32px;
      padding-top: 12px;
      border-top: 1px solid #eee;
      font-size: 11px;
      color: #aaa;
      text-align: center;
    }
    @media print {
      body { padding: 16px; }
      .msg { page-break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="export-header">
    <h1>${escapeHtml(genieName || 'AI Studio')} – Chat Export</h1>
    <div class="export-meta">
      User: ${escapeHtml(userName || 'User')} &nbsp;|&nbsp; Date: ${new Date().toLocaleString()}
    </div>
  </div>
  ${messageHTML}
  <div class="export-footer">Exported from AI Studio</div>
</body>
</html>`;

  // Open in new tab and trigger print dialog
  const printWindow = window.open('', '_blank', 'width=900,height=700');
  if (!printWindow) {
    alert('Popup blocked! Please allow popups for this site to export as PDF.');
    return;
  }
  printWindow.document.write(html);
  printWindow.document.close();
  // Replace "about:blank" in the print window's URL so browsers don't show
  // "about:blank" in the PDF footer/header when printing.
  try {
    printWindow.history.replaceState({}, '', window.location.href);
  } catch (e) {
    // replaceState may fail in some sandboxed environments; safe to ignore
  }
  printWindow.focus();
  // Give the browser a moment to render before printing
  setTimeout(() => {
    printWindow.print();
    // Don't auto-close — let the user finish with the dialog
  }, 400);
}

// ─── Helper ───────────────────────────────────────────────────────────────────
function escapeHtml(str) {
  const s = String(str ?? '');
  return s
    .replace(/&/g, '&')
    .replace(/</g, '<')
    .replace(/>/g, '>')
    .replace(/"/g, '"')
    .replace(/'/g, '&#x27;');
}
